import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Send, Loader2, Check, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

export default function KontaktPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formLoadedAt] = useState(() => new Date().toISOString());

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Bitte geben Sie Ihren Namen ein (min. 2 Zeichen)"); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Bitte geben Sie eine gueltige E-Mail-Adresse ein"); return; }
    if (!subject.trim() || subject.trim().length < 3) { toast.error("Bitte geben Sie einen Betreff ein (min. 3 Zeichen)"); return; }
    if (!message.trim() || message.trim().length < 10) { toast.error("Bitte schreiben Sie eine Nachricht (min. 10 Zeichen)"); return; }
    if (!privacy) { toast.error("Bitte stimmen Sie der Datenschutzerklaerung zu"); return; }

    setSending(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        privacy_accepted: privacy,
        honeypot,
        form_loaded_at: formLoadedAt,
      });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Bitte warten Sie etwas, bevor Sie eine weitere Nachricht senden.");
      } else {
        toast.error(err.response?.data?.detail || "Nachricht konnte nicht gesendet werden");
      }
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/50 bg-card/50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SpurwechselLogo size="xs" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="font-body text-sm gap-1" data-testid="kontakt-back-btn">
              <ArrowLeft className="w-4 h-4" /> Zurueck
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="kontakt-heading">Kontaktieren Sie uns</h1>
          <p className="font-body text-base text-muted-foreground mt-2">
            Wir antworten in der Regel innerhalb von 24 Stunden.
          </p>
        </div>

        {sent ? (
          <Card className="border-border/50" data-testid="kontakt-success">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-success" />
              </div>
              <p className="font-heading text-xl font-bold mb-2">Nachricht gesendet!</p>
              <p className="font-body text-sm text-muted-foreground mb-6">
                Vielen Dank fuer Ihre Nachricht. Wir melden uns in der Regel innerhalb von 24 Stunden bei Ihnen.
              </p>
              <Link to="/">
                <Button className="font-body bg-primary text-primary-foreground hover:bg-primary/90" data-testid="kontakt-back-home">
                  Zurueck zur Startseite
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <form onSubmit={submit} className="space-y-4" data-testid="kontakt-form">
                {/* Honeypot - hidden from users */}
                <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    value={honeypot}
                    onChange={e => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium">Name *</label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Ihr Name"
                    className="mt-1 h-11 font-body"
                    maxLength={100}
                    data-testid="kontakt-name"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium">E-Mail *</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    className="mt-1 h-11 font-body"
                    maxLength={200}
                    data-testid="kontakt-email"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium">Betreff *</label>
                  <Input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Worum geht es?"
                    className="mt-1 h-11 font-body"
                    maxLength={200}
                    data-testid="kontakt-subject"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium">Nachricht *</label>
                  <Textarea
                    value={message}
                    onChange={e => setMessage(e.target.value.slice(0, 2000))}
                    placeholder="Ihre Nachricht..."
                    className="mt-1 font-body text-sm min-h-[120px] resize-none"
                    data-testid="kontakt-message"
                  />
                  {message.length > 0 && (
                    <p className="font-body text-[10px] text-muted-foreground text-right mt-0.5">{message.length}/2000</p>
                  )}
                </div>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacy}
                    onChange={e => setPrivacy(e.target.checked)}
                    className="mt-1 rounded border-border"
                    data-testid="kontakt-privacy"
                  />
                  <span className="font-body text-xs text-muted-foreground leading-relaxed">
                    Ich stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu.{" "}
                    <Link to="/privacy" className="text-primary hover:underline">Datenschutzerklaerung</Link>
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full h-11 font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
                  data-testid="kontakt-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Nachricht senden
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="font-body text-xs text-muted-foreground text-center mt-6">
          Alternativ erreichen Sie uns per E-Mail: <a href="mailto:info@spurwechsel-pro.de" className="text-primary hover:underline">info@spurwechsel-pro.de</a>
        </p>
      </div>
    </div>
  );
}
